import { createElement, useCallback, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Loader2, Search, Trash2, UserPlus, Users } from 'lucide-react'
import { toast } from 'sonner'
import type {ColumnDef} from '@tanstack/react-table';
import type { WorkspaceRole } from '@/lib/types/roles'
import type { MemberDocument } from '@/lib/db/rxdb/hooks/entities/members'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { usePageLayout } from '@/hooks/usePageLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { memberAvatarSrc } from '@/lib/utils/memberAvatar'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DataTable,
  DataTableColumnHeader,
} from '@/components/ui/data-table'
import SettingsSidebar from '@/components/app/sidebars/settings'
import { SettingsPageContainer, SettingsPageHeader } from '@/components/app/layout'
import useLoginStore from '@/store/LoginStore'
import { useMembers } from '@/lib/db/rxdb/hooks/entities/members'
import {
  ROLE_DISPLAY,
  WORKSPACE_PERMISSIONS,
  WORKSPACE_ROLES,
} from '@/lib/types/roles'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import {
  removeMember,
  updateMemberRole,
} from '@/lib/api/features/workspace-members/api'
import { InviteMemberModal } from '@/components/workspace/InviteMemberModal'
import { InvitesList } from '@/components/workspace/InvitesList'
import { useIsApiDown } from '@/lib/api/useIsApiDown'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'

type TabKey = 'all' | 'members' | 'admins' | 'invites'

function getInitials(member: MemberDocument): string {
  if (member.name) {
    const parts = member.name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
    }
    return member.name[0]?.toUpperCase() ?? ''
  }
  if (member.email) {
    return member.email[0]?.toUpperCase() ?? ''
  }
  return '?'
}

function getMemberDisplayName(member: MemberDocument): string {
  if (member.name) return member.name
  if (member.email) return member.email
  return 'Unknown'
}

const ROLE_OPTIONS = [
  { value: WORKSPACE_ROLES.ADMIN, label: ROLE_DISPLAY.admin.label },
  { value: WORKSPACE_ROLES.MEMBER, label: ROLE_DISPLAY.member.label },
] as const

const ROLE_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  [WORKSPACE_ROLES.ADMIN]: 'default',
  [WORKSPACE_ROLES.MEMBER]: 'secondary',
  [WORKSPACE_ROLES.GUEST]: 'outline',
}

interface MemberRowContext {
  currentUserId: string
  adminCount: number
  canChangeRoles: boolean
  canRemoveMembers: boolean
  isApiDown: boolean
  updatingRoleId: string | null
  removingMemberId: string | null
  onRoleChange: (member: MemberDocument, role: string) => void
  onRequestRemove: (member: MemberDocument) => void
}

function RelativeTimeCell({ ts }: { ts: number }) {
  const { smartTime, dateTime: formatDateTime } = useDateTimeFormatters()
  const date = new Date(ts)
  return (
    <time
      className="text-xs text-muted-foreground"
      title={formatDateTime(date, { dateStyle: 'medium', timeStyle: 'short' })}
      dateTime={date.toISOString()}
    >
      {smartTime(date)}
    </time>
  )
}

function buildColumns(ctx: MemberRowContext): Array<ColumnDef<MemberDocument>> {
  return [
    {
      id: 'name',
      accessorFn: (row) => getMemberDisplayName(row),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      cell: ({ row }) => {
        const member = row.original
        const displayName = getMemberDisplayName(member)
        const isCurrentUser = member.userId === ctx.currentUserId
        return (
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="size-9 rounded-full shrink-0">
              <AvatarImage src={memberAvatarSrc(member)} alt={displayName} />
              <AvatarFallback className="rounded-full text-xs">
                {getInitials(member)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{displayName}</span>
                {FEATURE_FLAGS.ENABLE_SUPER_AGENT && member.isSuperAgent && (
                  <Badge className="shrink-0 bg-primary/10 text-[10px] uppercase tracking-wide text-primary hover:bg-primary/10">
                    AI
                  </Badge>
                )}
                {isCurrentUser && (
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    You
                  </Badge>
                )}
              </div>
              {member.email && (
                <div className="text-xs text-muted-foreground truncate">
                  {member.email}
                </div>
              )}
            </div>
          </div>
        )
      },
      filterFn: (row, _columnId, value: string) => {
        if (!value) return true
        const q = value.toLowerCase()
        const m = row.original
        return (
          (m.name ?? '').toLowerCase().includes(q) ||
          (m.email ?? '').toLowerCase().includes(q)
        )
      },
      sortingFn: (a, b) =>
        getMemberDisplayName(a.original).localeCompare(
          getMemberDisplayName(b.original),
        ),
    },
    {
      id: 'role',
      accessorFn: (row) => row.role ?? WORKSPACE_ROLES.MEMBER,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Role" />,
      cell: ({ row }) => {
        const member = row.original
        const memberRole = (member.role ?? WORKSPACE_ROLES.MEMBER) as WorkspaceRole
        const isCurrentUser = member.userId === ctx.currentUserId
        const isLastAdmin =
          memberRole === WORKSPACE_ROLES.ADMIN && ctx.adminCount <= 1
        const isUpdatingRole = ctx.updatingRoleId === member.id

        if (ctx.canChangeRoles && !isCurrentUser) {
          return (
            <Select
              value={memberRole}
              onValueChange={(value) => ctx.onRoleChange(member, value)}
              disabled={isLastAdmin || isUpdatingRole || ctx.isApiDown}
            >
              <SelectTrigger className="w-[120px] h-8">
                {isUpdatingRole ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <SelectValue />
                )}
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }

        return (
          <Badge variant={ROLE_BADGE_VARIANT[memberRole] ?? 'secondary'}>
            {ROLE_DISPLAY[memberRole].label}
          </Badge>
        )
      },
      filterFn: (row, _columnId, value: string) => {
        if (!value || value === 'all') return true
        const role = row.original.role ?? WORKSPACE_ROLES.MEMBER
        return role === value
      },
      enableSorting: false,
    },
    {
      id: 'lastActive',
      accessorFn: (row) => row.lastActiveAt ?? 0,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last Active" />
      ),
      cell: ({ row }) => {
        const ts = row.original.lastActiveAt
        if (!ts) {
          return <span className="text-xs text-muted-foreground">—</span>
        }
        return <RelativeTimeCell ts={ts} />
      },
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => {
        const member = row.original
        const memberRole = (member.role ?? WORKSPACE_ROLES.MEMBER) as WorkspaceRole
        const isCurrentUser = member.userId === ctx.currentUserId
        const isLastAdmin =
          memberRole === WORKSPACE_ROLES.ADMIN && ctx.adminCount <= 1
        const isRemoving = ctx.removingMemberId === member.id

        if (!ctx.canRemoveMembers || isCurrentUser || member.isSuperAgent) {
          return <div className="size-8" />
        }

        return (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground hover:text-destructive"
            disabled={isLastAdmin || isRemoving || ctx.isApiDown}
            title={ctx.isApiDown ? 'Unavailable while offline' : undefined}
            onClick={() => ctx.onRequestRemove(member)}
          >
            {isRemoving ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
          </Button>
        )
      },
      enableSorting: false,
    },
  ]
}

export default function MemberSettingsPage() {
  const { t } = useTranslation('settings')
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const workspaceName = useLoginStore(
    (s) =>
      s.workspaces.find((w) => w.workspaceSlug === s.workspaceSlug)?.name ??
      s.workspaceSlug,
  )
  const currentUserId = useLoginStore((s) => s.userId)

  const { members, isLoading } = useMembers()
  const isApiDown = useIsApiDown()

  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<MemberDocument | null>(null)
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null)
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [inviteListKey, setInviteListKey] = useState(0)

  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [search, setSearch] = useState('')

  const canChangeRoles = useHasPermission(WORKSPACE_PERMISSIONS.CHANGE_ROLES)
  const canRemoveMembers = useHasPermission(WORKSPACE_PERMISSIONS.REMOVE_MEMBERS)
  const canInviteMembers = useHasPermission(WORKSPACE_PERMISSIONS.INVITE_MEMBERS)

  // The Super Agent holds a workspace membership but is not a person. While Super Agent
  // is hidden it's dropped from the People list entirely — leaving the row would show a
  // bot as an ordinary member (editable role, counted in the tabs) with no AI badge to
  // explain it. It never consumed a seat (the members quota excludes it server-side),
  // and its access is cut by revoking the workspace token, not by this list.
  const activeMembers = useMemo(
    () =>
      members.filter(
        (m) =>
          !m.isDeleted &&
          (FEATURE_FLAGS.ENABLE_SUPER_AGENT || !m.isSuperAgent),
      ),
    [members],
  )

  const adminCount = useMemo(
    () => activeMembers.filter((m) => m.role === WORKSPACE_ROLES.ADMIN).length,
    [activeMembers],
  )

  const tabFilteredMembers = useMemo(() => {
    switch (activeTab) {
      case 'members':
        return activeMembers.filter((m) => m.role === WORKSPACE_ROLES.MEMBER)
      case 'admins':
        return activeMembers.filter((m) => m.role === WORKSPACE_ROLES.ADMIN)
      case 'all':
      default:
        return activeMembers
    }
  }, [activeMembers, activeTab])

  const handleRoleChange = useCallback(
    async (member: MemberDocument, newRole: string) => {
      if (!workspaceSlug) return

      setUpdatingRoleId(member.id)
      try {
        await updateMemberRole(workspaceSlug, member.id, { role: newRole })
        toast.success(
          `${getMemberDisplayName(member)}'s role updated to ${
            ROLE_DISPLAY[newRole as WorkspaceRole].label
          }`,
        )
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to update member role',
        )
      } finally {
        setUpdatingRoleId(null)
      }
    },
    [workspaceSlug],
  )

  const handleRemoveMember = useCallback(async () => {
    if (!workspaceSlug || !memberToRemove) return

    setRemovingMemberId(memberToRemove.id)
    try {
      await removeMember(workspaceSlug, memberToRemove.id)
      toast.success(
        `${getMemberDisplayName(memberToRemove)} has been removed from the workspace`,
      )
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove member',
      )
    } finally {
      setRemovingMemberId(null)
      setMemberToRemove(null)
    }
  }, [workspaceSlug, memberToRemove])

  const columns = useMemo(
    () =>
      buildColumns({
        currentUserId,
        adminCount,
        canChangeRoles,
        canRemoveMembers,
        isApiDown,
        updatingRoleId,
        removingMemberId,
        onRoleChange: handleRoleChange,
        onRequestRemove: setMemberToRemove,
      }),
    [
      currentUserId,
      adminCount,
      canChangeRoles,
      canRemoveMembers,
      isApiDown,
      updatingRoleId,
      removingMemberId,
      handleRoleChange,
    ],
  )

  const counts = useMemo(
    () => ({
      all: activeMembers.length,
      members: activeMembers.filter((m) => m.role === WORKSPACE_ROLES.MEMBER).length,
      admins: activeMembers.filter((m) => m.role === WORKSPACE_ROLES.ADMIN).length,
    }),
    [activeMembers],
  )

  return (
    <SettingsPageContainer>
      <SettingsPageHeader
        title="People"
        action={
          canInviteMembers && (
            <Button
              size="sm"
              onClick={() => setIsInviteModalOpen(true)}
              disabled={isApiDown}
              title={isApiDown ? 'Unavailable while offline' : undefined}
            >
              <UserPlus className="size-4 mr-2" />
              Invite people
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('membersPage.searchPlaceholder')}
            className="pl-8"
            disabled={activeTab === 'invites'}
          />
        </div>
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabKey)}
          className="ml-auto"
        >
          <TabsList>
            <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="members">Members ({counts.members})</TabsTrigger>
            <TabsTrigger value="admins">Admins ({counts.admins})</TabsTrigger>
            {canInviteMembers && (
              <TabsTrigger value="invites">Invites</TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </div>

      {activeTab === 'invites' ? (
        canInviteMembers ? (
          <InvitesList key={inviteListKey} workspaceSlug={workspaceSlug} />
        ) : null
      ) : isLoading ? (
        <div className="flex h-64 items-center justify-center rounded-md border bg-card">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={tabFilteredMembers}
          getRowId={(row) => row.id}
          globalFilter={search}
          onGlobalFilterChange={setSearch}
          initialSorting={[{ id: 'name', desc: false }]}
          emptyState={
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Users className="size-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No people match.</p>
            </div>
          }
        />
      )}

      {canInviteMembers && (
        <InviteMemberModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          workspaceSlug={workspaceSlug}
          workspaceName={workspaceName}
          onInviteSent={() => setInviteListKey((k) => k + 1)}
        />
      )}

      <AlertDialog
        open={memberToRemove !== null}
        onOpenChange={(open) => {
          if (!open) setMemberToRemove(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <span className="font-medium text-foreground">
                {memberToRemove ? getMemberDisplayName(memberToRemove) : ''}
              </span>{' '}
              from this workspace? They will lose access to all workspace
              resources.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingMemberId !== null}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={removingMemberId !== null}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removingMemberId !== null ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsPageContainer>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/members',
)({
  component: MemberSettingsPage,
})
